import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import {
  useCallback, useEffect, useMemo, useState
} from 'react';
import { TOASTIFY_TYPES, reactToastify } from 'common/utils/reactToastify';
import { MAX_CASES_IN_QUANT } from 'v2/common/constants/index';
import { getSelectorBasedStoreValue } from '../../redux/selector';
import { setCases, setPartialQuantConfig } from '../../redux/actions';
import { getDefaultCase, getQuantModes } from '../../configs/index';
import { getClonedCase } from './clone';
import useCasesError from './useCasesError';
import { updateCaseIndexes } from '../../events/updateCaseIndexes';

const useCases = () => {
  const cases = useSelector((state) =>
    getSelectorBasedStoreValue(state, 'quantConfig.cases')
  );
  const { errorConfigs, errorMessage } = useCasesError(cases);
  const pipeConfigs = useSelector((state) =>
    getSelectorBasedStoreValue(state, 'quantConfig.pipeConfigs')
  );
  const dispatch = useDispatch();
  const [activeCaseIndex, setActiveCaseIndex] = useState(0);
  const [selectedParallelCases, setSelectedParallelCases] = useState([]);

  const isMaximumCasesExceeded = cases.length >= MAX_CASES_IN_QUANT;

  const isMultipleCasesEnabled = _.get(window, 'SA_FEATURE_FLAGS.isMultipleCasesEnabled', false)
    || window?.isAdmin || false;

  const [isShowAddCaseHoverMessage, setIsShowAddCaseHoverMessage] = useState(false);

  const mode = useSelector((state) =>
    getSelectorBasedStoreValue(state, 'quantConfig.uiConfigs.mode')
  );
  const isHiddenSignal = useSelector((state) =>
    getSelectorBasedStoreValue(state, 'isHiddenSignal')
  );
  const isSimpleMode = mode === getQuantModes.simple.value;
  const instrumentGroups = useSelector((state) =>
    getSelectorBasedStoreValue(state, 'quantConfig.instrumentGroups')
  );

  // optional: auto hide after 5s
  useEffect(() => {
    if (isShowAddCaseHoverMessage) {
      const timer = setTimeout(() => setIsShowAddCaseHoverMessage(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [isShowAddCaseHoverMessage]);

  useEffect(() => setActiveCaseIndex(0), [isSimpleMode]);

  const parallelCasesOptions = useMemo(
    () => _.map(cases, (c, idx) => ({ id: idx, label: `Case ${idx + 1}` })),
    [cases]
  );

  useEffect(() => {
    const initiallySelected = _.filter(
      parallelCasesOptions,
      (opt) => cases[opt.id]?.isParallelCases
    );
    setSelectedParallelCases(initiallySelected);
  }, [parallelCasesOptions, cases]);

  const onChangeSelectedCases = (newSelectedOptions) => {
    const selectedIds = _.map(newSelectedOptions, 'id');
    const updatedParallelCases = _.map(cases, (c, idx) => ({
      ...c,
      isParallelCases: selectedIds.includes(idx),
    }));
    dispatch(setCases(updatedParallelCases));
  };

  const onAddCase = useCallback(() => {
    if (!isMultipleCasesEnabled) {
      setIsShowAddCaseHoverMessage(true);
      return;
    }
    if (isMaximumCasesExceeded) {
      reactToastify('You have reached the maximum cases.', TOASTIFY_TYPES.ERROR);
      return;
    }
    dispatch(setCases([...cases, getDefaultCase(cases.length, instrumentGroups)]));
    setActiveCaseIndex(cases.length);
  }, [cases, dispatch, isMultipleCasesEnabled, isMaximumCasesExceeded, instrumentGroups]);

  const onDeleteCase = useCallback(
    (deletedCaseIdx) => {
      const { updatedCases, updatedPipeConfigs } = updateCaseIndexes(
        cases,
        pipeConfigs,
        deletedCaseIdx
      );
      dispatch(
        setPartialQuantConfig({
          cases: updatedCases,
          pipeConfigs: updatedPipeConfigs,
        })
      );
      return true;
    },
    [cases, dispatch, pipeConfigs]
  );

  const onCloneCase = useCallback(
    (idx) => {
      if (!isMultipleCasesEnabled) {
        reactToastify(
          'Please purchase a pro or premium plan to use this feature',
          TOASTIFY_TYPES.ERROR
        );
        return;
      }
      if (isMaximumCasesExceeded) {
        reactToastify('You have reached the maximum cases.', TOASTIFY_TYPES.ERROR);
        return;
      }
      const newClonedCases = getClonedCase(cases, idx);
      newClonedCases.isParallelCases = false;
      dispatch(setCases([...cases, newClonedCases]));
    },
    [cases, dispatch, isMultipleCasesEnabled, isMaximumCasesExceeded]
  );

  return {
    cases,
    activeCaseIndex,
    onChangeActiveCaseIndex: setActiveCaseIndex,
    onAddCase,
    onDeleteCase,
    onCloneCase,
    isShowAddCaseHoverMessage,
    isSimpleMode,
    errorConfigs,
    errorMessage,
    isHiddenSignal,
    onChangeSelectedCases,
    parallelCasesOptions,
    selectedParallelCases,
    isMaximumCasesExceeded,
  };
};

export default useCases;
